import AggregateError from "es-aggregate-error";

import { DebugPromise } from "./DebugPromise";
import { errorToFields } from "./ErrorHandlingUtilities";
import { Logger } from "./Log";

class RecreatedError extends Error {
  constructor(message?: string, stack?: string) {
    super(message);

    this.name = "RecreatedError";
    this.stack = stack;
  }
}

// prettier-ignore
export enum HandlingMethod {
  DisregardError,
  HandleError
  // Extend with additional error-checking cases, and implement in the registrations below.
}

export interface ErrorHandlingReason {
  reason: string;
  handleConditions: Array<ErrorHandlingConditional>;
}

export interface ErrorHandlingConditional {
  method: HandlingMethod;
  isCondition: (error: Error) => boolean;
  onHandle?: (error?: Error) => void;
}

export enum FatalErrors {
  UserLoggedOut,
  NoWebGL,
  ServerIdleTimeout,
  ServerReconnectTimeout,
  ServerShuttingDown,
  WebsocketClosure,
  IncompatibleBrowser,
  Outage,
  // Extend with additional error-checking cases, and implement in the registrations below.
}

type SeenError = {
  type: string;
  value: unknown;
};

export class ErrorHandling {
  reasons = new Array<ErrorHandlingReason>();
  fatalErrors = new Map<FatalErrors, (errorCase: FatalErrors) => void>();
  private errorsSeen: SeenError[] = [];

  private static _instance: ErrorHandling;
  static get instance(): ErrorHandling {
    return this._instance;
  }

  private constructor(registerWindowErrorHandling: boolean) {
    if (registerWindowErrorHandling) {
      this.registerErrorHandling();
    }
  }

  static initialize(registerWindowErrorHandling = true): ErrorHandling {
    ErrorHandling._instance = new ErrorHandling(registerWindowErrorHandling);
    return ErrorHandling._instance;
  }

  static hasSeenError(error: SeenError): boolean {
    const hasSeen = ErrorHandling.instance.errorsSeen.some((e) => e.type === error.type && e.value === error.value);

    if (!hasSeen) ErrorHandling.instance.markErrorAsSeen(error);

    return hasSeen;
  }

  static fromCaught(error: unknown, area: string, recreatedError = "") {
    const errorToLog =
      error instanceof Error ? error : recreateError(typeof error === "string" ? error : recreatedError);
    resolveError(errorToLog, area);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static flattenErrors(error: any) {
    if (!error) {
      return [];
    }
    const errors = [error];
    if (hasNestedErrors(error)) {
      errors.unshift(...error.errors.flatMap(ErrorHandling.flattenErrors));
    }
    return errors;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  doConditionsPass(errorCase: HandlingMethod, error: Error): Array<() => void> | false {
    let handlers: Array<() => void> | undefined;

    if (this.reasons) {
      for (const reason of this.reasons) {
        for (const conditional of reason.handleConditions) {
          if (conditional.method === errorCase && conditional.isCondition(error)) {
            console.log(`[ErrorHandling] ${reason.reason}`);
            if (!handlers) handlers = [];
            if (conditional.onHandle) handlers.push(conditional.onHandle);
          }
        }
      }
    }

    return handlers ?? false;
  }

  registerErrorCase(reason: string, ...handleConditions: Array<ErrorHandlingConditional>): this {
    this.reasons.push({ reason, handleConditions });
    return this;
  }

  registerErrorCases(...reasons: Array<ErrorHandlingReason>): this {
    for (const reason of reasons) {
      this.reasons.push(reason);
    }
    return this;
  }

  registerFatalErrorHandling(errorCase: FatalErrors, handler: (errorCase: FatalErrors) => void) {
    this.fatalErrors.set(errorCase, handler);
    return this;
  }

  triggerFatalError(errorCase: FatalErrors) {
    const handler = this.fatalErrors.get(errorCase);
    if (handler) {
      handler(errorCase);
    }
  }

  private registerErrorHandling() {
    //Clear the error handling from before log client initialized before adding our normal handling
    // NOTE: using `new Function()` here to avoid restrictions
    // with strict mode and node 18.16 when running jest tests
    new Function("window.onerror = null")();
    window.iosCancelButtonPressed = false;

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
  }

  private markErrorAsSeen(error: SeenError) {
    this.errorsSeen.push(error);
  }
}

function recreateError(message: string, stack?: string): Error {
  return new RecreatedError(`[RecreatedError] ${message}`, stack);
}

function getErrorFrom(event: ErrorEvent & { source?: string }) {
  let errorToLog: Error;
  const additionalContext: Record<string, unknown> = {};

  if (hasNestedErrors(event.error)) {
    errorToLog = event.error;
    const errors = event.error.errors.flatMap(ErrorHandling.flattenErrors);
    additionalContext.history = errors.map(errorToFields);
  } else if (event.error) {
    errorToLog = event.error;
  } else if (event.message && event.source) {
    errorToLog = recreateError(event.message, `${event.message}\n  at ${event.source}:${event.lineno}:${event.colno}`);
  } else {
    errorToLog = recreateError(event.message ?? "No event message provided.");
  }

  return { error: errorToLog, additionalContext };
}

export function resolveError(error: Error | undefined, tag: string, additionalContext: Record<string, unknown> = {}) {
  if (!error) {
    return;
  }

  /** Handling for errors that will be disregarded. */
  const disregardHandlers = ErrorHandling.instance.doConditionsPass(HandlingMethod.DisregardError, error);
  if (disregardHandlers) {
    for (const handler of disregardHandlers) {
      handler();
    }
    return;
  }

  /** Attempt to construct or re-construct this error. */
  try {
    error.message = `[${tag}] ${error.message}`;
  } catch (err) {
    // `Cannot set property message of  which has only a getter` was showing up in the logs,
    // so just re-create the error instead of changing the message
    if (`${error}`.includes("DataCloneError: The object can not be cloned.")) {
      error = recreateError(`[${tag}] ${error}`, `${error.stack ?? ""} ${JSON.stringify(additionalContext)}`);
    } else {
      error = recreateError(`[${tag}] ${error}`, error.stack);
    }
  }

  /** It's an error that we want to handle! */

  // NOTE: putting in a simple log for now. It's possible to add more context (action history, location history)
  // but that'll involve dispatching an action for `web/src/pods/error/data/logging` to handle
  Logger.logException(error, additionalContext);

  const errorHandlers = ErrorHandling.instance.doConditionsPass(HandlingMethod.HandleError, error);
  if (errorHandlers) {
    for (const handler of errorHandlers) {
      handler();
    }
  }
}

export function hasNestedErrors(err: unknown) {
  if (!err) {
    return false;
  }
  type ErrorNestedErrors = { errors: unknown[] };
  const hasMoreErrors = (err: unknown): err is ErrorNestedErrors => (err as ErrorNestedErrors).errors !== undefined;
  return err instanceof AggregateError || hasMoreErrors(err);
}

// Note: I attempted to create an overloaded "getErrorFrom" function taking
//    a union type of PromiseRejectionEvent and ErrorEvent, however I ran into
//    a frustrating issue: Jest could not recognize PromiseRejectionEvent when
//    provided as an argument of `instanceof` in an if statement.
//    Quite bizarre. This was not a found issue at transpile-time, nor at runtime.
//    Strictly an issue with Jest, perhaps because of its usage of jsdom.
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getErrorFromPromiseRejection(event: PromiseRejectionEvent) {
  let errorToLog: Error;
  const additionalContext: Record<string, unknown> = {};

  if (hasNestedErrors(event.reason)) {
    errorToLog = event.reason;
    const errors = event.reason.errors.flatMap(ErrorHandling.flattenErrors);
    additionalContext.history = errors.map(errorToFields);
  } else if (event.reason instanceof Error) {
    errorToLog = event.reason;
  } else if (event.reason) {
    errorToLog = recreateError(event.reason);
  } else {
    let stack: Error["stack"];
    if (DebugPromise.unreasonableRejectStacks != undefined) {
      if (event.promise instanceof DebugPromise) {
        const promiseId = event.promise?.promiseId;
        if (promiseId != undefined) {
          stack = DebugPromise.unreasonableRejectStacks[promiseId].stack;
        }
      }
      if (stack == undefined) {
        stack = JSON.stringify(DebugPromise.unreasonableRejectStacks);
      }
      // Stop collecting altogether.
      // Note that the code in onUnhandledRejection that checks hasSeenError ends up ignoring more than a single unreasonable reject.
      DebugPromise.unreasonableRejectStacks = undefined;
    }

    if (stack !== undefined) {
      errorToLog = new Error(
        `Unknown error in promise rejection v3.\n ts:${event.timeStamp}\n promise:${JSON.stringify(
          event.promise
        )}\n stack:${stack}`
      );
      errorToLog.stack = stack;
      console.log("ErrorHandling.getErrorFromPromiseRejection\n", errorToLog);
    } else {
      errorToLog = new Error("Unknown error in promise rejection v3.");
    }
  }

  return { error: errorToLog, additionalContext };
}

function onWindowError(event: ErrorEvent & { source?: string }) {
  // Custom handling for a window-only error, seen on Firefox when running hub-client `mojo watch` (Vite, dev mode).
  // If you're not seeing this error while running in Firefox, go ahead and delete this code.
  const DEBUG_LOG = typeof window !== "undefined" && window?._ENV !== "prod";
  if (DEBUG_LOG && event.message === "SyntaxError: import declarations may only appear at top level of a module") {
    console.error(
      "Due to issues with Vite dev mode, worker modules, and Firefox, only Chrome/Chromium is presently supported for local Monsterverse testing.\n\n" +
        "- For more info, read details outlined in this present issue:\n  - https://github.com/vitejs/vite/issues/8470 \n" +
        "- Bug tracker on Firefox:\n  - https://bugzilla.mozilla.org/show_bug.cgi?id=1247687 \n" +
        "- Current support as indicated on MDN:\n  - https://developer.mozilla.org/en-US/docs/Web/API/Worker#browser_compatibility"
    );
  }

  // prevent double logging of same error
  if (ErrorHandling.hasSeenError({ type: event.type, value: event.error })) return;
  const { error, additionalContext } = getErrorFrom(event);
  resolveError(error, "GLOBAL_ERROR", additionalContext);
}

function onUnhandledRejection(event: PromiseRejectionEvent) {
  // prevent double logging of same error
  if (ErrorHandling.hasSeenError({ type: event.type, value: event.reason })) return;
  const { error, additionalContext } = getErrorFromPromiseRejection(event);
  resolveError(error, "GLOBAL_REJECTION", additionalContext);
}
